import React, { useEffect, useState } from 'react';
import { AssignPositionTableContext, IAssignPositionTableContext } from './AssignPositionTableContext';
import { useAsyncData } from '@shared/react';
import { getAssignedPosition, getBrokerLevelClient } from '../../api';
import { usePagination2 } from '@features/Pagination/usePagination';
import HandleSecurityRefreshStore from '@widgets/BrokerLevelPositionsTabs/store/handle-refresh-security-store';
import { combineFilterWithOp } from '@shared/utils';
import { getLogo } from '@widgets/TransactionsTable/api';
import useDateRangeStore from '@widgets/BrokerLevelPositionsTabs/store/date-range-filter-store';
import SecurityStore from '@widgets/BrokerLevelPositionsTabs/store/security-store';
import {
    getAllBrokerLevelSecurities,
    getBrokerLevelPositions,
} from '@widgets/BrokerLevelPositionsTabs/components/BrokerLevelPositionsTable/api';

export interface ITransactionDesc {
    id: boolean;
    accountId: boolean;
    orderId: boolean;
    date: boolean;
    price: boolean;
    settlementDate: boolean;
    quantity: boolean;
    type: boolean;
    execId: boolean;
    processed: boolean;
    [key: string]: boolean;
}

export const AssignPositionTableProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [ClientDetailsIdentifier, setClientDetailsIdentifier] = useState<string>('');
    const [securityType, setSecurityType] = useState<any>('');

    const [accountId, setAccountId] = useState<any>(null);
    const [cusip, setCusip] = useState<string>('');
    const [dateRange, setDateRange] = useState<[string | null, string | null]>(['', '']);
    const [sortFieldName, setSortFieldName] = useState('id');
    const [userType, setUserType] = useState<string | null>(null);
    const refreshSecurity = HandleSecurityRefreshStore();
    const dateRanges = useDateRangeStore();
    const securities = SecurityStore();
    const setSecurities = SecurityStore((state) => state.setSecurities);
    const setRiaSecurities = SecurityStore((state) => state.setRiaSecurities);
    const [desc, setDesc] = useState<ITransactionDesc>({
        id: true,
        accountId: true,
        orderId: true,
        date: true,
        price: true,
        settlementDate: true,
        quantity: true,
        type: true,
        execId: true,
        processed: true,
    });

    const { page, pageSize, setTotalCount, paginationOptions } = usePagination2();

    const userStorage = localStorage.getItem('User');
    let userObject: any = null;
    let query: any = null;
    userObject = JSON.parse(userStorage ? userStorage : '');
    if (userObject?.accounts) {
        if (userObject.userRole === 5) {
            query = {
                page,
                pageSize,
                sortBy: `${sortFieldName} ${desc[sortFieldName] ? 'desc' : ''}`,
                filter: `AccountId=${userObject.accounts[0].id}`,
            };
        } else {
            query = {
                page,
                pageSize,
                sortBy: `${sortFieldName} ${desc[sortFieldName] ? 'desc' : ''}`,
                filter: `AssignerId = ${userObject.accounts[0].id}`,
            };
        }
    } else {
        query = {
            page,
            pageSize,
            sortBy: `${sortFieldName} ${desc[sortFieldName] ? 'desc' : ''}`,
        };
    }

    const fetchSecurities = async () => {
        // Make the function async
        if (!securities.securities?.length && userObject.userRole === 4) {
            let securityQuery;

            const accountId = userObject.accounts?.[0]?.id || null; // Get the first account's ID or null

            const combinedAccounts = [accountId]; // Only include accountId if subAccountIds is empty

            // Filter out null values
            const filteredAccounts = combinedAccounts.filter((id) => id !== null);

            securityQuery = {
                page,
                pageSize,
                sortBy: `${sortFieldName} ${desc[sortFieldName] ? 'desc' : ''}`,
                filter: combineFilterWithOp({
                    AccountId: {
                        value: filteredAccounts.length && ` ${filteredAccounts.join(';')}`,
                        operator: ' #In',
                    },
                }),
            };

            const { items, totalResults } = await getBrokerLevelClient(securityQuery);
            setRiaSecurities(items);
        }

        // Make the function async
        if (!securities.securities?.length) {
            let items = await getAllBrokerLevelSecurities();
            setSecurities(items);
        }
    };

    const { data: AssignPositionBody, handleRefresh: securityRefresh } = useAsyncData(
        async () => {
            if (accountId !== null) {
                fetchSecurities();
                const securityTypeFilter = securityType.length > 0 ? `SecurityId #In ${securityType.join(';')}` : '';
                if (accountId !== 0) {
                    query.filter = `AccountId=${accountId}`;
                }
                const filterString = [
                    query.filter,
                    securityTypeFilter,
                    dateRanges.dateRange[0] && dateRanges.dateRange[1] // Add date range filter only if both dates are present
                        ? `CreatedDate >= ${dateRanges.dateRange[0]} , CreatedDate < ${dateRanges.dateRange[1]}`
                        : null,
                ]
                    .filter(Boolean)
                    .join(' , ');

                query.filter = filterString;

                const { items, totalResults } = await getAssignedPosition(query);

                setTotalCount(totalResults);
                return items || [];
            } else {
                return [];
            }
        },
        [],
        [pageSize, page, ClientDetailsIdentifier, cusip, dateRange, userType, securityType, dateRanges, accountId],
    );

    const { data: arrayWithLogos, loading: logoLoading } = useAsyncData(
        async () => {
            return await Promise.all(
                AssignPositionBody.map(async (item) => {
                    const res = await getLogo({ symbol: item.security.symbol });
                    return { link: res, symbol: item.security.symbol };
                }),
            );
        },
        [],
        [AssignPositionBody],
    );

    useEffect(() => {
        if (refreshSecurity.refreshSecurity) {
            securityRefresh();
        }
    }, [refreshSecurity]);

    const value: IAssignPositionTableContext = {
        dateRange,
        AssignPositionBody,
        paginationOptions,
        arrayWithLogos,
        sortFieldName,
        desc,
        userType,
        setSecurityType,
        setClientDetailsIdentifier,
        setCusip,
        setDateRange,
        setUserType,
        setAccountId,
    };

    return <AssignPositionTableContext.Provider value={value}>{children}</AssignPositionTableContext.Provider>;
};
